import {
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type RefObject,
  type ReactNode,
} from 'react'
import { NavLink, useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '@/auth/useAuth'
import { GLOBAL_ROLE_LABELS, type Permission } from '@/auth/permissions'
import { useTheme } from '@/lib/theme'
import { useSimulation } from '@/sim/SimulationProvider'
import { packCapacity, type GaitKind, type Simulation } from '@/domain/hs1'
import { Backdrop } from '@/components/stage'
import { DashHud } from '@/components/stage/dash/DashHud'
import { DashKnock } from '@/components/stage/dash/DashKnock'
import { useDash } from '@/components/stage/dash/DashProvider'
import { poseFor } from '@/components/stage/framing'
import { Avatar, Badge, IconButton } from '@/components/ui'
import { Reticle } from './Reticle'
import { UpdateHint } from './UpdateHint'
import {
  IconAnalysis,
  IconArchitecture,
  IconAssembly,
  IconBalance,
  IconChannels,
  IconClearance,
  IconConcept,
  IconDocs,
  IconDrives,
  IconRoadmap,
  IconMarket,
  IconMoon,
  IconOverview,
  IconParameters,
  IconPartsList,
  IconPlm,
  IconVersions,
  IconPractice,
  IconRecorder,
  IconAudit,
  IconPeople,
  IconSignOut,
  IconSoftware,
  IconSpaces,
  IconStyleguide,
  IconSun,
  IconTestbed,
  IconChain,
} from './icons'
import './shell.css'

/**
 * Rahmen der Anwendung.
 *
 * Der Aufbau ist bewusst nicht der übliche: keine Seitenleiste, kein Rahmen um
 * den Inhalt. Der 4NE-1 füllt den Hintergrund, alles andere schwebt als
 * Glasfläche darüber — Kopfleiste oben, Navigation als Kacheln unten, Inhalt
 * dazwischen.
 *
 * `<Backdrop />` hängt hier und nicht in einer Route: so bleibt der
 * WebGL-Kontext beim Seitenwechsel bestehen und die Figur dreht durch, statt
 * bei jeder Navigation neu aufzusetzen.
 *
 * Die Navigation ist nach Rechten gefiltert. Das ist Bequemlichkeit, keine
 * Sicherheit — wer den Pfad eintippt, läuft in den RequirePermission-Guard und
 * danach in die RLS. Aber ein Dock voller Kacheln, die alle in eine
 * Fehlermeldung führen, wäre unbrauchbar.
 */

function NeuraMark() {
  return (
    <svg className="brand-mark" viewBox="0 0 28 28" aria-hidden="true">
      <path
        d="M5 22V6l18 16V6"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.6"
        strokeLinecap="square"
        strokeLinejoin="miter"
      />
    </svg>
  )
}

interface NavEntry {
  to: string
  label: string
  icon: ReactNode
  /**
   * Farbton der Kachel als reine Zahl (HSL-Winkel).
   *
   * Jedes Modul bekommt seinen eigenen — die Farbe ist hier ein Wiedererkennungs-
   * merkmal, kein Schmuck: bei zehn Kacheln findet man „die violette" schneller
   * als „die vierte von links".
   *
   * Sättigung und Helligkeit liegen im CSS und sind für alle gleich, damit die
   * Reihe trotz zehn Farbtönen als ein Satz liest und nicht als Regenbogen.
   * Auftreten dürfen sie nur als LICHT an der Kante — nie als Füllfarbe.
   */
  hue: number
  permission?: Permission
  end?: boolean
}

/**
 * Das Dock — jeder Reiter des Simulators ein Hauptmenüpunkt.
 *
 * WAS NICHT DRIN IST, IST AUCH EINE AUSSAGE: die Verwaltungsseiten aus dem
 * ursprünglichen Intranet-Entwurf und der Styleguide stehen nicht mehr hier.
 * Sie sind weiter erreichbar, nur nicht mehr im Weg.
 *
 * Die Reihenfolge folgt der Kette und nicht dem Alphabet: erst die Eingaben
 * (Parameter — was die Maschine ist und was ein Mensch daran verstellt), dann
 * was dabei herauskommt (Simulation, Analyse, Antriebe). Die nachschlagenden
 * Reiter stehen dahinter.
 *
 * Modell, Parameter und Merkmale waren drei Kacheln und sind eine: die
 * Trennung war eine des Rechenwegs und keine der Bedienung.
 *
 * Achtzehn Kacheln sind viel. Sie sind es, weil die Vorlage so viele Reiter
 * hat und jeder davon eine eigene Frage beantwortet — sie zu Untermenüs
 * zusammenzufassen hiesse, eine Ebene einzuziehen, die niemand verlangt hat.
 * Das Dock scrollt seitwärts und hält die aktive Kachel im Blick.
 */
const NAV: NavEntry[] = [
  { to: '/', label: 'Übersicht', icon: <IconOverview />, hue: 190, end: true },

  /*
    DIE UMSETZUNG STEHT VORN, GLEICH NEBEN DER ÜBERSICHT.

    Sie gehört nicht zum Rechenweg — sie sagt, was gebaut wird, bis wann und
    woran es abgenommen wird. Hinten zwischen den Nachschlagewerken war sie die
    Kachel, die man suchen musste; sie wird häufiger gebraucht als jede
    einzelne Rechenseite.

    Die einzige Simulatorkachel mit einer Rechteprüfung — siehe `roadmap:read`
    in `permissions.ts`. Das Ausblenden ist Bequemlichkeit; die Route trägt
    denselben Guard.
  */
  {
    to: '/umsetzung',
    label: 'Umsetzung',
    icon: <IconRoadmap />,
    hue: 44,
    permission: 'roadmap:read',
  },

  /* Der Kern: Eingaben, Verstellung, Ergebnis. */
  { to: '/parameter', label: 'Parameter', icon: <IconParameters />, hue: 168 },
  { to: '/simulation', label: 'Simulation', icon: <IconChannels />, hue: 38 },
  /*
    HIER STAND „KANÄLE".

    Die Kanalauswahl ist kein eigener Bereich, sondern der Vorrat der
    Simulation: dort wird bestimmt, welche Kurven auf der Simulationsseite
    stehen. Als eigene Kachel sah sie aus wie ein zweiter Ort für dieselbe
    Sache. Erreichbar ist sie über die Auswahlleiste der Simulation; die Seite
    selbst bleibt unter /kanaele.
  */
  { to: '/analyse', label: 'Analyse', icon: <IconAnalysis />, hue: 265 },
  { to: '/antriebe', label: 'Antriebe', icon: <IconDrives />, hue: 340 },

  /* Was auf dem Kern aufbaut. */
  { to: '/recorder', label: 'Recorder', icon: <IconRecorder />, hue: 12 },
  { to: '/freigang', label: 'Freigang', icon: <IconClearance />, hue: 128 },
  { to: '/balance', label: 'Balance', icon: <IconBalance />, hue: 88 },
  { to: '/stueckliste', label: 'Stückliste', icon: <IconPartsList />, hue: 232 },
  { to: '/plm', label: 'PLM', icon: <IconPlm />, hue: 300 },
  { to: '/staende', label: 'Stände', icon: <IconVersions />, hue: 200 },
  /*
    DIE EINZIGE KACHEL, DIE IN DIE ANDERE RICHTUNG RECHNET.

    Alle übrigen zeigen dieselbe Rechnung aus verschiedenen Blickwinkeln: die
    Bewegung ist vorgegeben, die Kräfte folgen. Der Prüfstand gibt die Momente
    vor und lässt die Bewegung sich einstellen — Station 6 der Werkzeugkette,
    im Browser statt auf der Grafikkarte.
  */
  { to: '/pruefstand', label: 'Prüfstand', icon: <IconTestbed />, hue: 176 },
  /*
    Was zwischen diesem Simulator und einem laufenden Roboter liegt — und was
    davon gebaut ist. Steht neben dem Prüfstand, weil der die erste Station
    ausserhalb dieser Anwendung ist.
  */
  { to: '/kette', label: 'Werkzeugkette', icon: <IconChain />, hue: 100 },

  /* Nachschlagewerke. */
  { to: '/dokumentation', label: 'Grundlagen', icon: <IconDocs />, hue: 212 },
  { to: '/aufbau', label: 'Aufbau', icon: <IconAssembly />, hue: 24 },
  { to: '/architektur', label: 'Architektur', icon: <IconArchitecture />, hue: 288 },
  { to: '/konzept', label: 'Konzept', icon: <IconConcept />, hue: 150 },
  /*
    HIER STAND „P00 KNIE".

    Die Ausarbeitung eines EINZELNEN Gelenks gehört nicht neben Grundlagen,
    Aufbau und Architektur — das sind Nachschlagewerke über die ganze Maschine.
    Sie gehört an das Gelenk: der Achsbaum führt am Knie den Knopf
    „Ausarbeitung", und von dort ist sie einen Klick entfernt. Sonst steht im
    Dock eine Kachel je ausgearbeitetem Gelenk, sobald es die zweite gibt.

    Die Seite selbst bleibt unter /p00 erreichbar; nur der Eintrag im Dock ist
    weg.
  */
  { to: '/software', label: 'Software', icon: <IconSoftware />, hue: 248 },
  { to: '/markt', label: 'Marktdossier', icon: <IconMarket />, hue: 320 },
  { to: '/praxis', label: 'Praxis', icon: <IconPractice />, hue: 56 },

  /*
    HIER STANDEN VIER WEITERE KACHELN: Bereiche, Personen, Protokoll,
    Styleguide.

    Sie sind aus dem Dock genommen und NICHT gelöscht. Die Routen gibt es
    weiter, ebenso das Schema und die RLS dahinter — wer `/spaces`,
    `/admin/users`, `/admin/audit` oder `/styleguide` eintippt, kommt an, und
    die Rechteprüfung greift wie zuvor.

    Der Grund ist die Reihe selbst. Sie stammen aus dem ursprünglichen
    Intranet-Entwurf, bevor der Simulator das Projekt wurde, und beantworten
    keine Frage über den Gen5. Neben zwanzig Kacheln, die genau das tun,
    kosten sie Platz und Aufmerksamkeit — und der Styleguide ist ein Werkzeug
    der Entwicklung, kein Inhalt für NEURA.

    Erreichbar sind sie über das Nutzermenü in der Kopfleiste — siehe
    `ACCOUNT_LINKS`.
  */
]

/**
 * Was zum Konto gehört und nicht zur Maschine.
 *
 * Diese vier standen bis dahin im Dock. Sie beantworten keine Frage über den
 * Gen5, und im Nutzermenü stehen sie neben dem, wozu sie gehören: Name,
 * Rechte, Bereiche.
 */
const ACCOUNT_LINKS: NavEntry[] = [
  { to: '/konto', label: 'Mein Konto', icon: <IconPeople />, hue: 0, end: true },
  { to: '/spaces', label: 'Bereiche', icon: <IconSpaces />, hue: 0 },
  { to: '/admin/users', label: 'Personen', icon: <IconPeople />, hue: 0, permission: 'user:read' },
  { to: '/admin/audit', label: 'Protokoll', icon: <IconAudit />, hue: 0, permission: 'audit:read' },
  { to: '/styleguide', label: 'Styleguide', icon: <IconStyleguide />, hue: 0 },
]

/**
 * Hält die aktive Kachel im Sichtfeld.
 *
 * Bei vielen Modulen scrollt das Dock seitwärts. Wer über die Tastatur oder
 * einen Deep Link auf eine Seite kommt, deren Kachel gerade außerhalb liegt,
 * sähe sonst ein Dock ohne erkennbaren aktiven Zustand.
 */
function useScrollActiveIntoView(dependency: string) {
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const active = ref.current?.querySelector<HTMLElement>('.dock__item.active')
    active?.scrollIntoView({ block: 'nearest', inline: 'center', behavior: 'smooth' })
  }, [dependency])

  return ref
}

/** Reichweite der Vergrösserung, waagerecht in Pixeln. */
const FISHEYE_RADIUS = 190

/** Ab dieser Entfernung über dem Dock wirkt nichts mehr. */
const FISHEYE_REACH_Y = 190

/** Reichweite der seitlichen Anziehung. */
const MAGNET_RADIUS = 150

/**
 * Das Fischauge des Docks — dieselbe Bewegung wie im Dock von Mac OS.
 *
 * WAS ES TUT. Die Kacheln stehen klein nebeneinander; unter dem Zeiger wächst
 * eine von ihnen auf volle Grösse, die Nachbarn wachsen weniger, deren
 * Nachbarn noch weniger. Das Ergebnis ist keine hervorgehobene Kachel, sondern
 * eine Linse, die über die Reihe fährt.
 *
 * WARUM ES NICHT MIT `:hover` GEHT. Ein Hover kennt genau zwei Zustände und
 * genau eine Kachel. Man kann die beiden Nachbarn über `+` mitnehmen — das war
 * die vorige Fassung —, aber es bleibt eine Treppe aus drei Stufen, und sie
 * springt, sobald der Zeiger die Grenze zwischen zwei Kacheln überquert. Ein
 * Fischauge ist eine stetige Funktion des ABSTANDS und braucht deshalb die
 * Zeigerposition, nicht den Zustand „darüber".
 *
 * DER ABSTAND ZÄHLT WAAGERECHT. Senkrecht entscheidet nur, OB die Linse wirkt:
 * je weiter der Zeiger über dem Dock steht, desto flacher wird sie, und
 * oberhalb der Reichweite ist sie aus. Ohne dieses Tor bliebe das Dock
 * dauerhaft verformt, während man eine Tabelle am oberen Seitenrand liest.
 *
 * GESCHRIEBEN WIRD EIN ANTEIL, KEINE GRÖSSE. Das JavaScript setzt `--fish`
 * zwischen 0 und 1; wie gross eine Kachel dabei wird und wie weit sie sich
 * hebt, steht in `shell.css`. Die Zahlen des Aussehens gehören ins CSS —
 * sonst muss man für eine grössere Kachel eine Schleife anfassen.
 *
 * Der Zeiger wird an EINEM Knoten abgehört, nicht an zwanzig: zwanzig Handler,
 * die alle dasselbe Ereignis bekommen, sind zwanzigmal dieselbe Rechnung.
 */
function useDockFisheye(ref: RefObject<HTMLDivElement>) {
  useEffect(() => {
    const scroller = ref.current
    if (!scroller) return
    if (!window.matchMedia('(pointer: fine)').matches) return
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return

    let raf = 0
    let pointer: { x: number; y: number } | null = null

    const clear = (tile: HTMLElement) => {
      tile.style.removeProperty('--fish')
      tile.style.removeProperty('--pull-x')
      tile.style.removeProperty('--pull-y')
    }

    const apply = () => {
      raf = 0
      const tiles = scroller.querySelectorAll<HTMLElement>('.dock__item')

      if (!pointer) {
        tiles.forEach(clear)
        return
      }

      const at = pointer
      const strip = scroller.getBoundingClientRect()
      /* Wie weit der Zeiger ÜBER dem Dock steht. Innerhalb der Leiste ist das
         null, darüber wächst es; unterhalb gibt es nichts mehr. */
      const above = Math.max(0, strip.top - at.y)
      const gate = Math.max(0, 1 - above / FISHEYE_REACH_Y)

      if (gate === 0) {
        tiles.forEach(clear)
        return
      }

      tiles.forEach((tile) => {
        const box = tile.getBoundingClientRect()
        const dx = at.x - (box.left + box.width / 2)
        const dy = at.y - (box.top + box.height / 2)

        /*
          Die Linse: eine halbe Kosinuswelle über der Reichweite. Sie ist an
          beiden Enden flach — genau das unterscheidet eine Linse von einem
          Kegel. Ein linearer Abfall hätte am Rand der Reichweite einen Knick,
          und der ist als Ruck in der Bewegung zu sehen.
        */
        const t = Math.min(1, Math.abs(dx) / FISHEYE_RADIUS)
        const fish = 0.5 * (1 + Math.cos(Math.PI * t)) * gate
        tile.style.setProperty('--fish', fish.toFixed(3))

        /* Die seitliche Anziehung bleibt: sie neigt die Reihe zum Zeiger, wo
           das Fischauge sie nur aufbläst. Zusammen ergibt das eine Oberfläche,
           die auf Annäherung reagiert, statt einer Reihe wachsender Kästen. */
        const distance = Math.hypot(dx, dy)
        if (distance > MAGNET_RADIUS) {
          tile.style.setProperty('--pull-x', '0px')
          tile.style.setProperty('--pull-y', '0px')
          return
        }
        const pull = (1 - distance / MAGNET_RADIUS) ** 2
        tile.style.setProperty('--pull-x', `${(dx * pull * 0.22).toFixed(2)}px`)
        tile.style.setProperty('--pull-y', `${(dy * pull * 0.12).toFixed(2)}px`)
      })
    }

    const schedule = () => {
      if (!raf) raf = requestAnimationFrame(apply)
    }

    const onMove = (event: PointerEvent) => {
      pointer = { x: event.clientX, y: event.clientY }
      schedule()
    }

    const onLeave = () => {
      pointer = null
      schedule()
    }

    // Am Fenster, nicht an der Leiste: die Kacheln sollen sich schon strecken,
    // BEVOR der Zeiger sie erreicht.
    window.addEventListener('pointermove', onMove, { passive: true })
    document.addEventListener('pointerleave', onLeave)
    /* Beim Scrollen wandern die Kacheln unter einem stehenden Zeiger hindurch.
       Ohne das Nachrechnen bliebe die Linse an der Stelle stehen, an der sie
       zuletzt gerechnet wurde, und läge dann neben dem Zeiger. */
    scroller.addEventListener('scroll', schedule, { passive: true })

    return () => {
      window.removeEventListener('pointermove', onMove)
      document.removeEventListener('pointerleave', onLeave)
      scroller.removeEventListener('scroll', schedule)
      if (raf) cancelAnimationFrame(raf)
    }
  }, [ref])
}

/**
 * Seitwärts scrollen mit dem Rad.
 *
 * DAS DOCK IST EINE ZEILE, UND EIN MAUSRAD DREHT SENKRECHT. Ohne diese
 * Übersetzung erreicht man mit einer gewöhnlichen Maus die Kacheln am Rand
 * überhaupt nicht — nur mit Trackpad-Wischen oder Tastatur. Bei zwanzig
 * Kacheln sind das die letzten fünf.
 *
 * `deltaX` bleibt unangetastet: wer waagerecht wischt, scrollt ohnehin schon
 * richtig, und beides zu addieren machte aus einer diagonalen Bewegung einen
 * doppelten Vorschub.
 */
function useWheelScroll(ref: RefObject<HTMLDivElement>) {
  useEffect(() => {
    const scroller = ref.current
    if (!scroller) return

    const onWheel = (event: WheelEvent) => {
      if (event.deltaX !== 0) return
      if (event.deltaY === 0) return
      const max = scroller.scrollWidth - scroller.clientWidth
      if (max <= 1) return
      /* Nur abfangen, wenn es hier noch etwas zu scrollen GIBT. Am Ende der
         Leiste soll das Rad wieder die Seite bewegen, statt ins Leere zu
         greifen. */
      const next = scroller.scrollLeft + event.deltaY
      if ((next < 0 && scroller.scrollLeft === 0) || (next > max && scroller.scrollLeft === max)) {
        return
      }
      event.preventDefault()
      scroller.scrollLeft = Math.max(0, Math.min(max, next))
    }

    scroller.addEventListener('wheel', onWheel, { passive: false })
    return () => scroller.removeEventListener('wheel', onWheel)
  }, [ref])
}

/** Breite der Zone an jedem Ende der Leiste, in der die Leiste nachrückt. */
const EDGE_ZONE = 120

/** Höchstgeschwindigkeit ganz aussen, in Pixeln je Sekunde. */
const EDGE_SPEED = 1100

/**
 * Gegen den Rand drücken, und die Leiste rückt nach.
 *
 * WOZU, NEBEN DEM RAD. Das Rad braucht eine Hand an der Maus UND eine Drehung;
 * wer die letzte Kachel ansteuern will, dreht dreimal und zielt dann. Hier
 * genügt es, den Zeiger ans Ende der Leiste zu schieben und stehen zu lassen —
 * dieselbe Geste, mit der man in einer Zeitleiste oder einer Karte an den Rand
 * fährt. Die Kachel kommt zum Zeiger statt umgekehrt.
 *
 * DIE GESCHWINDIGKEIT HÄNGT DAVON AB, WIE WEIT AUSSEN DER ZEIGER STEHT, und
 * zwar quadratisch. Am inneren Rand der Zone kriecht die Leiste, ganz aussen
 * läuft sie. Linear wäre sie innen schon zu schnell, um eine bestimmte Kachel
 * zu treffen — und aussen zu langsam, um zwanzig zu überspringen.
 *
 * ZWEI TORE, DAMIT SIE NICHT VON SELBST LOSLÄUFT:
 *
 *   – Der Zeiger muss AUF der Leiste stehen, senkrecht. Sonst zöge das Dock
 *     los, während jemand am unteren Bildrand eine Tabelle liest.
 *   – In der Richtung muss noch etwas übrig sein. Am Anschlag hört sie auf,
 *     statt gegen die Wand zu rechnen.
 *
 * WARUM DER REST MITGEZÄHLT WIRD (`carry`). Bei kleiner Geschwindigkeit ist
 * der Vorschub eines Takts ein Bruchteil eines Pixels. Wer den wegwirft,
 * schreibt sechzigmal in der Sekunde Null — die Leiste stünde still, obwohl
 * sie kriechen sollte.
 *
 * DER TAKT IST EIN TIMER UND NICHT `requestAnimationFrame`: der Vorschub soll
 * nicht an der Bildausgabe hängen, denn wo die stockt, stockt er mit.
 *
 * Wichtiger als die Wahl des Takts ist aber, dass die verstrichene ZEIT
 * rechnet und nicht die Zahl der Takte. Im Prüfstand kamen nur zwei Takte je
 * Sekunde an — Software-Rendering, ausgelasteter Hauptfaden, und
 * `requestAnimationFrame` lag dort bei denselben zwei. Die Leiste rückte
 * trotzdem, nur langsamer. Wer stattdessen je Takt eine feste Zahl Pixel
 * setzte, hätte auf einem solchen Rechner ein Dreissigstel der Geschwindigkeit
 * — und auf einem schnellen das Doppelte.
 *
 * NICHT ABGESCHALTET BEI `prefers-reduced-motion`. Das Fischauge daneben schon,
 * denn es ist Schmuck. Das hier ist ein Bedienweg: er läuft nur, solange
 * jemand den Zeiger absichtlich dort hält, und hört auf, sobald er ihn
 * wegnimmt. Ihn zu streichen nähme einer Maus ohne Rad den Zugang zu den
 * äusseren Kacheln.
 */
function useEdgeScroll(ref: RefObject<HTMLDivElement>) {
  useEffect(() => {
    const scroller = ref.current
    if (!scroller) return
    /* Ein Finger kann nicht „am Rand stehen bleiben" — dort wird gewischt. */
    if (!window.matchMedia('(pointer: fine)').matches) return

    let timer = 0
    let last = 0
    let carry = 0
    let pointer: { x: number; y: number } | null = null

    /** Vorschub in Pixeln je Sekunde; negativ nach links, 0 = still. */
    const speed = (): number => {
      if (!pointer) return 0
      const box = scroller.getBoundingClientRect()
      if (pointer.y < box.top || pointer.y > box.bottom) return 0

      const max = scroller.scrollWidth - scroller.clientWidth
      if (max <= 1) return 0

      /* Auch jenseits der Kante: wer den Zeiger an den Bildschirmrand drückt,
         steht ausserhalb der Leiste und meint trotzdem genau das. */
      const left = Math.min(1, (box.left + EDGE_ZONE - pointer.x) / EDGE_ZONE)
      const right = Math.min(1, (pointer.x - (box.right - EDGE_ZONE)) / EDGE_ZONE)

      if (left > 0 && scroller.scrollLeft > 0) return -EDGE_SPEED * left * left
      if (right > 0 && scroller.scrollLeft < max) return EDGE_SPEED * right * right
      return 0
    }

    const stop = () => {
      if (timer) window.clearInterval(timer)
      timer = 0
      carry = 0
    }

    const tick = () => {
      const now = performance.now()
      /* Die Schranke fängt eine Pause ab — ein weggeklicktes Fenster, ein
         hängender Takt. Ohne sie holte der erste Schritt danach die ganze
         Pause auf einmal nach, und die Leiste spränge. */
      const dt = Math.min(0.1, (now - last) / 1000)
      last = now

      const v = speed()
      if (v === 0) {
        stop()
        return
      }

      const step = v * dt + carry
      const whole = Math.trunc(step)
      carry = step - whole
      if (whole !== 0) {
        const max = scroller.scrollWidth - scroller.clientWidth
        scroller.scrollLeft = Math.max(0, Math.min(max, scroller.scrollLeft + whole))
      }
    }

    const start = () => {
      if (timer) return
      last = performance.now()
      carry = 0
      timer = window.setInterval(tick, 16)
    }

    const onMove = (event: PointerEvent) => {
      if (event.pointerType !== 'mouse') return
      pointer = { x: event.clientX, y: event.clientY }
      if (speed() !== 0) start()
    }

    const onLeave = () => {
      pointer = null
      stop()
    }

    window.addEventListener('pointermove', onMove, { passive: true })
    document.addEventListener('pointerleave', onLeave)
    /* Wer das Fenster wegklickt, während der Zeiger am Rand steht, käme sonst
       zu einer Leiste zurück, die inzwischen weitergelaufen ist. */
    window.addEventListener('blur', onLeave)

    return () => {
      window.removeEventListener('pointermove', onMove)
      document.removeEventListener('pointerleave', onLeave)
      window.removeEventListener('blur', onLeave)
      stop()
    }
  }, [ref])
}

/**
 * Die Brücke zwischen Spiel und Simulation.
 *
 * SIE STEHT HIER UND NICHT IM PROVIDER, weil sie die einzige Stelle ist, an der
 * das Spiel etwas an der Anwendung VERSTELLT: es setzt die Gangart der laufenden
 * Simulation. Alles andere liest nur. Diese eine Richtung sichtbar zu lassen —
 * statt sie im Provider zu verstecken — ist der Grund für die eigene Komponente.
 *
 * Beim Beenden wird zurückgestellt, was vorher stand. Wer nach einer Runde eine
 * Seite öffnet und dort einen Sprint vorfindet, den er nie eingestellt hat,
 * sucht den Fehler im Rechenkern.
 */
function DashGame() {
  const dash = useDash()
  const { state, simulation, setGait, gaitRun } = useSimulation()
  const before = useRef<GaitKind | null>(null)
  /* Die Zeitgeber unten laufen erst spaeter ab; ohne die Referenz riefen sie
     die Fassung aus dem Bild auf, in dem das Spiel geoeffnet wurde. */
  const vorrechnen = useRef(gaitRun)
  vorrechnen.current = gaitRun

  useEffect(() => {
    if (!dash.running) {
      if (before.current !== null) {
        setGait(before.current)
        before.current = null
      }
      return
    }
    if (before.current === null) before.current = state.gait
  }, [dash.running, state.gait, setGait])

  useEffect(() => {
    if (dash.running) setGait(dash.gait)
  }, [dash.running, dash.gait, setGait])

  /*
    DIE DREI GANGARTEN WERDEN AN DER STARTLINIE VORGERECHNET.

    Ein Gangwechsel im Lauf war als Hakeln zu sehen, und der Grund war kein
    Zeichenfehler: der Kern rechnet den neuen Gang, den Bodenkontakt, die
    Dynamik und die Antriebe erst dann. Das ist ein Bild, das ausfällt — genau
    in dem Moment, in dem die Figur sichtbar umsetzt.

    Beim Öffnen des Spiels steht der Läufer noch. Ein Bild, das dort fehlt,
    fällt niemandem auf, und danach ist jeder Wechsel ein Nachschlagen. Die
    beiden anderen Gangarten liegen ein Bild auseinander, damit nicht drei
    Durchläufe zu einer Pause zusammenlaufen.
  */
  useEffect(() => {
    if (!dash.running) return
    /* Bewusst nur an `running`: bei jedem Gangwechsel neu vorzurechnen wäre
       genau die Rechnung, die hier vermieden werden soll. */
    const uhren = (['walk', 'run', 'sprint'] as const).map((g, i) =>
      window.setTimeout(() => vorrechnen.current(g), 150 + i * 150),
    )
    return () => uhren.forEach(window.clearTimeout)
  }, [dash.running])

  /*
    DIE LEISTUNG WIRD ABGELESEN UND NICHT NACHGERECHNET.

    Hier stand `meanPower(simulate(state))` — ein vollständiger Durchlauf des
    Rechenkerns, bei JEDEM Render dieser Komponente. Und sie rendert bei jeder
    Meldung des Spielzustands, also etwa zehnmal je Sekunde. Gang, Bodenkontakt,
    Dynamik und Antriebe wurden zehnmal je Sekunde neu gerechnet, um eine Zahl
    anzuzeigen, die längst im Provider steht. Genau das war das Ruckeln.

    `simulation` IST der Durchlauf zum aktuellen Zustand. Es gibt nichts
    nachzurechnen.
  */
  const power = meanPower(simulation)

  /*
    Der Akku hängt am Modell und nicht am Spielverlauf: ausgelegt nach dem
    GEHEN, deshalb ein eigener Durchlauf.

    Über `gaitRun` und nicht über `simulate({ ...state, gait: 'walk' })`, und
    das ist kein Geschmack: dort blieben `trim` und `task` die der laufenden
    Gangart. Der Zustand wäre nach jedem Wechsel ein anderer, und die
    Akkuauslegung liefe bei JEDEM Gangwechsel neu — ein voller Durchlauf des
    Kerns für eine Zahl, die sich gar nicht ändert. Das war die Hälfte des
    Hakelns.
  */
  const capacity = packCapacity(meanPower(gaitRun('walk')))

  return (
    <DashHud
      store={dash.store}
      gait={dash.gait}
      power={power}
      capacity={capacity}
      legend={dash.legend}
      onLegend={dash.setLegend}
      onGait={dash.setGait}
      onJump={dash.requestJump}
      onRestart={dash.restart}
      onQuit={dash.stop}
      onRetry={dash.retry}
      onGo={dash.requestJump}
    />
  )
}

/** Mittlere Antriebsleistung eines Durchlaufs, W. */
const meanPower = (sim: Simulation): number =>
  sim.metrics.cycleTime > 0 ? Math.max(0, sim.power.work) / sim.metrics.cycleTime : 0

export function AppShell({ children }: { children: ReactNode }) {
  const { profile, globalRole, permissions, spaces, signOut } = useAuth()
  const { theme, toggle } = useTheme()
  const navigate = useNavigate()
  const location = useLocation()
  const [menuOpen, setMenuOpen] = useState(false)
  const dash = useDash()

  // Dieselbe Quelle wie die Kamerafahrt: die Pose bestimmt, wie viel Platz die
  // Figur im Bild einnimmt — und damit, wie breit der Inhalt laufen darf.
  // Zwei getrennte Tabellen dafuer waeren zwei Wahrheiten.
  const pose = poseFor(location.pathname)

  const dockRef = useScrollActiveIntoView(location.pathname)
  useDockFisheye(dockRef)
  useWheelScroll(dockRef)
  useEdgeScroll(dockRef)

  const entries = NAV.filter((e) => !e.permission || permissions.has(e.permission))
  const displayName = profile?.full_name || profile?.email || 'Unbekannt'

  async function handleSignOut() {
    await signOut()
    navigate('/login', { replace: true })
  }

  return (
    <div
      className={['shell', dash.running ? 'shell--dash' : ''].filter(Boolean).join(' ')}
      data-pose={pose.key}
    >
      <a className="skip-link" href="#content">
        Zum Inhalt springen
      </a>

      <Backdrop />
      {!dash.running && <Reticle />}
      {!dash.running && <UpdateHint />}

      <header className="topbar">
        {/* Sieben Klicks hierauf öffnen das Easter Egg. Die Wortmarke bleibt
            äusserlich unverändert — kein Zeiger, kein Hover, keine Rolle. */}
        <DashKnock className="topbar__brand">
          <NeuraMark />
          <span className="topbar__brand-text">
            <span className="wordmark topbar__brand-name">NEURA</span>
            <span className="topbar__brand-sub">Performance</span>
          </span>
        </DashKnock>

        <div className="topbar__actions">
          <IconButton
            label={theme === 'dark' ? 'Zu hellem Design wechseln' : 'Zu dunklem Design wechseln'}
            onClick={toggle}
          >
            {theme === 'dark' ? <IconSun /> : <IconMoon />}
          </IconButton>

          <div className="topbar__user">
            <button
              type="button"
              className="topbar__user-button"
              onClick={() => setMenuOpen((open) => !open)}
              aria-expanded={menuOpen}
              aria-haspopup="true"
            >
              <Avatar name={displayName} src={profile?.avatar_url} size="sm" />
              <span className="topbar__user-name">{displayName}</span>
            </button>

            {menuOpen && (
              <>
                <button
                  className="topbar__menu-scrim"
                  aria-label="Menü schließen"
                  onClick={() => setMenuOpen(false)}
                />
                <div className="topbar__menu" role="menu">
                  <div className="topbar__menu-head">
                    <span className="topbar__menu-name">{displayName}</span>
                    <span className="topbar__menu-mail numeric">{profile?.email}</span>
                    {globalRole && (
                      <Badge tone="accent" outline>
                        {GLOBAL_ROLE_LABELS[globalRole]}
                      </Badge>
                    )}
                  </div>
                  <p className="topbar__menu-meta">
                    {permissions.size} globale Rechte · {spaces.length}{' '}
                    {spaces.length === 1 ? 'Bereich' : 'Bereiche'}
                  </p>
                  {/*
                    DIE VERWALTUNG SITZT HIER UND NICHT MEHR IM DOCK.

                    Sie gehört zum Konto und nicht zum Gen5 — und im Dock stand
                    sie zwischen zwanzig Kacheln, die alle eine Frage über die
                    Maschine beantworten. Ein getippter Pfad wäre als einziger
                    Weg dorthin zu wenig: was es gibt, muss auffindbar sein.

                    Gefiltert wird wie im Dock nach Rechten — Bequemlichkeit,
                    keine Sicherheit. Wer den Pfad kennt, läuft weiterhin in den
                    RequirePermission-Guard und danach in die RLS.
                  */}
                  {ACCOUNT_LINKS.filter((e) => !e.permission || permissions.has(e.permission)).map(
                    (entry) => (
                      <NavLink
                        key={entry.to}
                        to={entry.to}
                        role="menuitem"
                        className="topbar__menu-item"
                        onClick={() => setMenuOpen(false)}
                      >
                        {entry.icon}
                        {entry.label}
                      </NavLink>
                    ),
                  )}

                  <button
                    type="button"
                    role="menuitem"
                    className="topbar__menu-item"
                    onClick={handleSignOut}
                  >
                    <IconSignOut />
                    Abmelden
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </header>

      <main className="content" id="content">
        {/* Der Schluessel am Wechsel des Pfades: React baut den Inhalt beim
            Modulwechsel neu auf, und die Einblendung laeuft erneut. Ohne ihn
            behielte React den Knoten und die Animation feuerte nur beim ersten
            Mal — der Wechsel saehe aus wie ein Sprung, waehrend die Kamera im
            Hintergrund noch faehrt. */}
        <div className="content__inner" key={location.pathname}>
          {children}
        </div>
      </main>

      {/* Die Anzeige des Spiels liegt über allem. Sie erscheint nur im Spiel;
          `DashHud` gibt sonst null zurück. */}
      <DashGame />

      <nav className="dock" aria-label="Hauptnavigation">
        {/*
          MIT DEM RAD SEITWÄRTS.

          Die Leiste läuft über den Rand hinaus und scrollt waagerecht. Ein
          Trackpad kann das von sich aus, ein Mausrad nicht: es erzeugt eine
          SENKRECHTE Bewegung, und die tut in einer waagerechten Leiste nichts.
          Wer nur eine Maus hat, kam an die äusseren Kacheln nicht heran.

          Umgesetzt wird nur, was sonst verfiele — waagerechte Bewegung eines
          Trackpads bleibt unangetastet, und am Ende der Leiste greift die
          Umsetzung nicht, damit die Seite darunter weiter scrollen kann.
        */}
        <div
          className="dock__scroller"
          ref={dockRef}
          onWheel={(event) => {
            const node = event.currentTarget
            if (Math.abs(event.deltaX) > Math.abs(event.deltaY)) return
            const rest = node.scrollWidth - node.clientWidth
            if (rest <= 0) return
            const next = node.scrollLeft + event.deltaY
            if (next <= 0 || next >= rest) return
            event.preventDefault()
            node.scrollLeft = next
          }}
        >
          {entries.map((entry) => (
            <NavLink
              key={entry.to}
              to={entry.to}
              end={entry.end}
              className="dock__item"
              title={entry.label}
              style={{ '--tile-hue': entry.hue } as CSSProperties}
            >
              <span className="dock__icon">{entry.icon}</span>
              <span className="dock__label">{entry.label}</span>
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  )
}
